import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { dataFields, schema } from "./utils";
import * as S from "./StepOne.styles";
import RegisterService from "../../../../../Register/service";
import { setPersonalData } from "../../../../../../redux/slices/personalDataSlice";
import {
  setIsValidForm,
  setFormPendingLoading,
  setStep,
} from "../../../../../../redux/slices/formSlice";
import requiredFields from "./utils/requiredFields";
import trimData from "./utils/trimData";
import InputService from "../../../../../../functions/InputService/InputService";
import * as Masks from "../../../../../../utils/masks";
import dadosPessoaisIcon from "../../../../../../images/icons-figma/dados-pessoais.svg";
import enderecoIcon from "../../../../../../images/icons-figma/endereco.svg";

const StepOne = ({ formRef }) => {
  const dispatch = useDispatch();
  const { loadingGetForm } = useSelector((state) => state.form);
  const { address, ...rest } = useSelector((state) => state.personalData);

  const [lockedFields, setLockedFields] = useState([]);
  const isFieldLocked = (fieldName) => lockedFields.includes(fieldName);

  const defaultValues = { ...address, ...rest };

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    clearErrors,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
    mode: "onSubmit",
  });

  const inputService = new InputService(control, errors);

  const values = watch(requiredFields);

  useEffect(() => {
    const allFieldsFilled = values.every(
      (value) => value != null && String(value).trim() !== ""
    );

    dispatch(setIsValidForm(allFieldsFilled));
  }, [values, dispatch]);

  const onSubmit = useCallback(
    async (data) => {
      const trimmedData = trimData(data);
      dispatch(setPersonalData(trimmedData));
      dispatch(setStep(2));
      dispatch(setIsValidForm(false));
    },
    [dispatch]
  );

  useEffect(() => {
    if (formRef.current) {
      formRef.current.onsubmit = handleSubmit(onSubmit);
    }
  }, [formRef, handleSubmit, onSubmit]);


  const handleCepChange = async (event) => {
    const cep = Masks.cep.mask(event.target.value);
    setValue("postal_code", cep);

    if (cep.length < 9) {
      setLockedFields([]);
    }

    if (cep.length === 9) {
      dispatch(setFormPendingLoading(true));
      setLockedFields([]);

      try {
        const cepWithoutMask = Masks.cep.unmask(cep);

        const { data } = await RegisterService.getAddressFromCep(cepWithoutMask);
        const { bairro, logradouro, uf, localidade } = data;

        setValue("street", logradouro);
        setValue("state", uf);
        setValue("city", localidade);
        setValue("neighborhood", bairro);

        clearErrors(["street", "state", "city", "postal_code", "neighborhood"]);

        const fieldsToLock = [];
        if (logradouro?.trim()) fieldsToLock.push("street");
        if (uf?.trim()) fieldsToLock.push("state");
        if (localidade?.trim()) fieldsToLock.push("city");
        if (bairro?.trim()) fieldsToLock.push("neighborhood");

        setLockedFields(fieldsToLock);
      } catch (error) {
        setLockedFields([]);
      } finally {
        dispatch(setFormPendingLoading(false));
      }
    }
  };

  const handlePhoneChange = (event) => {
    const phone = Masks.phone.mask(event.target.value);
    setValue("phone", phone);
  };

  const handleCpf = (e) => {
    const value = Masks.cpf.mask(e.target.value);
    setValue("cpf", value);
  };

  return (
    <S.StepOneContainer>
      <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
        <div>
          <div className="title-container personal-data">
            <img src={dadosPessoaisIcon} alt="" />
            <p>Dados Pessoais</p>
          </div>
          {inputService.renderInput(dataFields.full_name)}
          <div className="inputs-side-by-side">
            {inputService.renderInput({ ...dataFields.cpf, internalOnChange: handleCpf })}
            {inputService.renderInput({
              ...dataFields.phone,
              internalOnChange: handlePhoneChange,
            })}
          </div>
          {inputService.renderInput(dataFields.email)}
        </div>
        <div>
          <div className="title-container address-container">
            <img src={enderecoIcon} alt="" />
            <p>Endereço</p>
          </div>
          {inputService.renderInput({
            internalOnChange: handleCepChange,
            ...dataFields.postalCode,
            disabled: loadingGetForm,
            loading: loadingGetForm,
          })}
          <div className={loadingGetForm ? "loading" : ""}>
            {inputService.renderInput({
              ...dataFields.street,
              disabled: isFieldLocked("street") || loadingGetForm,
            })}
          </div>
          <div className="inputs-side-by-side number-row">
            {inputService.renderInput(dataFields.number)}
            {inputService.renderInput(dataFields.complement)}
          </div>
          <div className={loadingGetForm ? "loading" : ""}>
            {inputService.renderInput({
              ...dataFields.neighborhood,
              disabled: isFieldLocked("neighborhood") || loadingGetForm,
            })}
          </div>
          <div className={`inputs-side-by-side ${loadingGetForm ? "loading" : ""}`}>
            {inputService.renderInput({
              ...dataFields.city,
              disabled: isFieldLocked("city") || loadingGetForm,
            })}
            {inputService.renderInput({
              ...dataFields.state,
              disabled: isFieldLocked("state") || loadingGetForm,
            })}
          </div>
        </div>
      </form>
    </S.StepOneContainer>
  );
};

export default StepOne;
